import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import {
  FeatureSubscriptionStatus,
  useFeatureSubscription_GetPortalSeatSubscriptionQuery,
  useFeatureSubscription_GetSeatQuantityQuery,
  useUser_GetPortalAccountUsersQuery,
  useUser_GetPortalOwnerByPortalIdQuery,
} from 'queries';

import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';
import useIsImpersonating from 'utils/useIsImpersonating';

export default function SubscriptionAccessControlModal() {
  const { portalId } = useParams<Record<string, string>>();

  const currentUser = useCurrentUser();
  const isImpersonating = useIsImpersonating();

  const { data: portalOwnerData, loading: portalOwnerLoading } =
    useUser_GetPortalOwnerByPortalIdQuery({
      variables: {
        portalId: portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting portal owner: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { data: subscriptionData, loading: subscriptionLoading } =
    useFeatureSubscription_GetPortalSeatSubscriptionQuery({
      variables: {
        portalId: portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting portal seat subscription: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { data: seatQuantityData, loading: seatQuantityLoading } =
    useFeatureSubscription_GetSeatQuantityQuery({
      variables: {
        portalId: portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting seat quantity: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { data: usersData, loading: usersLoading } = useUser_GetPortalAccountUsersQuery({
    variables: {
      input: {
        portalId,
        includeAccountDisabledUsers: false,
        includeClientUsers: false,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal account users: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!currentUser) {
    return <Spinner />;
  }
  if (!portalOwnerData || portalOwnerLoading) {
    return <Spinner />;
  }
  if (!subscriptionData || subscriptionLoading) {
    return <Spinner />;
  }
  if (!seatQuantityData || seatQuantityLoading) {
    return <Spinner />;
  }
  if (!usersData || usersLoading) {
    return <Spinner />;
  }

  const accountOwner = `${portalOwnerData.data.firstName} ${portalOwnerData.data.lastName}`;
  const accountOwnerEmail = portalOwnerData.data.email;

  const getAccessControlMessage = (): string | null => {
    // Check the active number of users does not exceed the active number of seats
    const activeUsers = usersData.data || [];

    // Don't show access control modal when there is only one active user
    if (activeUsers.length <= 1) {
      return null;
    }

    // Then check if there is an active subscription
    if (subscriptionData.data?.featureSubscriptionStatus !== FeatureSubscriptionStatus.Active) {
      return `This account no longer has an active user plan. Please contact the account owner ${accountOwner} at ${accountOwnerEmail} for more details.`;
    }

    // Then check the active number of users does not exceed the active number of seats
    if (activeUsers.length > (seatQuantityData.data || 1)) {
      return `This account has hit or exceeded active user limit. Please contact the account owner ${accountOwner} at ${accountOwnerEmail} for more details.`;
    }

    // Don't show access control modal when the subscription is "active" AND the user count is less than or equal to the subscription product quantity
    return null;
  };

  const message = getAccessControlMessage();
  const showModal = !!message;

  // Never show the access control modal when:
  //  1. TSA is impersonating OR..
  //  2. The user is a superadmin OR..
  //  3. The user is portalOwner
  if (isImpersonating || currentUser.isSuperAdmin || currentUser.id === portalOwnerData.data.id) {
    return <></>;
  }

  return (
    <div>
      <Modal
        setShowModal={() => {}}
        onExitClick={() => {}}
        showModal={showModal}
        content={
          <div>
            <Typography variant="h2">Access Restricted</Typography>
            <Typography>{message}</Typography>
          </div>
        }
        showActions={false}
        actionDisabled={true}
        maxWidth="lg"
        overrideDialogStyles={{ backdropFilter: 'blur(10px)' }}
      />
    </div>
  );
}
